import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';

export interface SpecialButtonConfig {
  label?: string;
  icon?: any; // FontAwesome icon prop
  onClick: () => void;
  disabled?: boolean;
  ariaLabel?: string;
  variant?: string | null;
  bgColor?: string;
  className?: string;
  showText?: boolean;
}

interface SpecialButtonsContextType {
  special1: SpecialButtonConfig | null;
  special2: SpecialButtonConfig | null;
  special3: SpecialButtonConfig | null;
  register: (slot: 'special1' | 'special2' | 'special3', config: SpecialButtonConfig | null) => void;
  clear: () => void;
}

const SpecialButtonsContext = createContext<SpecialButtonsContextType | undefined>(undefined);

export const useSpecialButtons = () => {
  const context = useContext(SpecialButtonsContext);
  // return null if context is not available (for backwards compatibility)
  if (!context) {
    return null;
  }
  return context;
};

interface SpecialButtonsProviderProps {
  children: ReactNode;
}

export const SpecialButtonsProvider: React.FC<SpecialButtonsProviderProps> = ({ children }) => {
  const [special1, setSpecial1] = useState<SpecialButtonConfig | null>(null);
  const [special2, setSpecial2] = useState<SpecialButtonConfig | null>(null);
  const [special3, setSpecial3] = useState<SpecialButtonConfig | null>(null);

  const register = useCallback((slot: 'special1' | 'special2' | 'special3', config: SpecialButtonConfig | null) => {
    if (slot === 'special1') {
      // use functional setter to avoid redundant updates
      setSpecial1(prev => prev === config ? prev : config);
    } else if (slot === 'special2') {
      setSpecial2(prev => prev === config ? prev : config);
    } else {
      setSpecial3(prev => prev === config ? prev : config);
    }
  }, []);

  const clear = useCallback(() => {
    setSpecial1(null);
    setSpecial2(null);
    setSpecial3(null);
  }, []);

  // memoize the provider value to avoid creating a new object on every render
  const value = useMemo(() => ({
    special1,
    special2,
    special3,
    register,
    clear
  }), [special1, special2, special3, register, clear]);

  return (
    <SpecialButtonsContext.Provider value={value}>
      {children}
    </SpecialButtonsContext.Provider>
  );
};
